import { FC, JSX, useState } from 'react';
import { ZodFormattedError } from 'zod';

import Check from '@semcore/icon/Check/m';

import type { TextBlock } from '@/entities/content/api/types';
import Button from '@/shared/ui/button';
import DeleteIcon from '@/shared/ui/icons/Delete';
import Input from '@/shared/ui/Input';

import { TextBlockSchema } from '../../lib/schema';

import DeleteModal from './DeleteModal';

interface ICreateTextBlockFormProps {
  textBlock: TextBlock | null;
  onRemove: () => void;
  onSave: (textBlock: TextBlock) => void;
}

const CreateTextBlockForm: FC<ICreateTextBlockFormProps> = ({
  textBlock,
  onRemove,
  onSave,
}): JSX.Element => {
  const [removeConfirmationModalVisible, setRemoveConfirmationModalVisible] =
    useState(false);
  const modalCloseHandler = (): void => {
    setRemoveConfirmationModalVisible(false);
  };
  const [newTextBlock, setNewTextBlock] = useState<TextBlock>(
    textBlock ?? {
      start: 0,
      end: 0,
      url: '',
      content: '',
    }
  );
  const [errors, setErrors] = useState<ZodFormattedError<TextBlock> | null>(
    null
  );
  const onSaveHandler = () => {
    const res = TextBlockSchema.safeParse(newTextBlock);

    if (!res.success) {
      setErrors(res.error.format());

      return;
    }

    onSave(res.data);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <h6 className="flex grow text-sm font-semibold">Creating text block</h6>
        <DeleteModal
          visible={removeConfirmationModalVisible}
          onClose={modalCloseHandler}
          triggerDelete={onRemove}
        />
        <Button
          variant="function"
          size="sm"
          iconAfter={<DeleteIcon className="stroke-black hover:stroke-black" />}
          className="rounded-lg bg-blue/[.02] px-3 py-2"
          onClick={() => setRemoveConfirmationModalVisible(true)}
        />
        <Button
          variant="function"
          size="sm"
          title="Save"
          iconAfter={<Check />}
          className="rounded-lg bg-blue/[.02] px-3 py-2"
          onClick={onSaveHandler}
        />
      </div>
      <div className="flex gap-2">
        <div className="flex w-32">
          <Input
            type="number"
            error={
              // eslint-disable-next-line no-underscore-dangle
              errors?.start?._errors?.[0]
            }
            placeholder="Start"
            errorClassName={
              // eslint-disable-next-line no-underscore-dangle
              errors?.start?._errors?.length ? 'text-sm font-light' : 'hidden'
            }
            className="border border-gray/[0.5] bg-white"
            value={newTextBlock.start}
            onChange={(e) =>
              setNewTextBlock((prev) => ({
                ...prev,
                start: parseInt(e.target.value, 10),
              }))
            }
          />
        </div>
        <div className="flex w-32">
          <Input
            type="number"
            error={
              // eslint-disable-next-line no-underscore-dangle
              errors?.end?._errors?.[0]
            }
            placeholder="End"
            errorClassName={
              // eslint-disable-next-line no-underscore-dangle
              errors?.end?._errors?.length ? 'text-sm font-light' : 'hidden'
            }
            className="border border-gray/[0.5] bg-white"
            value={newTextBlock.end}
            onChange={(e) =>
              setNewTextBlock((prev) => ({
                ...prev,
                end: parseInt(e.target.value, 10),
              }))
            }
          />
        </div>
        <Input
          type="url"
          error={
            // eslint-disable-next-line no-underscore-dangle
            errors?.url?._errors?.[0]
          }
          placeholder="URL"
          errorClassName={
            // eslint-disable-next-line no-underscore-dangle
            errors?.url?._errors?.length ? 'text-sm font-light' : 'hidden'
          }
          className="flex grow border border-gray/[0.5] bg-white"
          value={newTextBlock.url}
          onChange={(e) =>
            setNewTextBlock((prev) => ({
              ...prev,
              url: e.target.value,
            }))
          }
        />
      </div>
      <div className="flex">
        <Input
          type="text"
          error={
            // eslint-disable-next-line no-underscore-dangle
            errors?.content?._errors?.[0]
          }
          placeholder="Text content"
          errorClassName={
            // eslint-disable-next-line no-underscore-dangle
            errors?.content?._errors?.length ? 'text-sm font-light' : 'hidden'
          }
          className="border border-gray/[0.5] bg-white"
          value={newTextBlock.content}
          onChange={(e) =>
            setNewTextBlock((prev) => ({
              ...prev,
              content: e.target.value,
            }))
          }
        />
      </div>
    </>
  );
};

export default CreateTextBlockForm;
